// frontend/src/components/QuickAddNoteDialog.js

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert
} from '@mui/material';
import { Mic as MicIcon, Stop as StopIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useRecording } from '../contexts/SimpleRecordingContext';
import axios from '../api/axiosConfig';

const QuickAddNoteDialog = ({ open, onClose }) => {
  const { currentUser } = useAuth();
  const { 
    isRecording, 
    currentTranscript, 
    toggleRecording, 
    connectionStatus 
  } = useRecording();
  
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [useVoice, setUseVoice] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Update content with transcript
  React.useEffect(() => {
    if (useVoice && currentTranscript) {
      setContent(currentTranscript);
      // Auto-generate title from first line if empty
      if (!title && currentTranscript) {
        const firstLine = currentTranscript.split('\n')[0].substring(0, 50);
        setTitle(firstLine);
      }
    }
  }, [currentTranscript, useVoice, title]);

  const handleToggleVoice = () => {
    setUseVoice(!useVoice);
    if (!useVoice) {
      // Clear content when switching to voice mode
      setContent('');
    }
  };

  const handleToggleRecording = () => {
    if (useVoice) {
      toggleRecording();
    }
  };

  const handleSubmit = async () => {
    if (!title.trim() && !content.trim()) {
      setError('Please enter a title or content for the note');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = await currentUser.getIdToken();
      const noteData = {
        title: title.trim(),
        content: content.trim(),
        type: useVoice ? 'voice' : 'text'
      };

      await axios.post('/api/notes', noteData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Success - reset form and close
      setTitle('');
      setContent('');
      setUseVoice(false);
      setError('');
      
      // Stop recording if active
      if (isRecording) {
        toggleRecording();
      }
      
      onClose();
      
      // Show success message
      console.log('Note added successfully');
    } catch (error) {
      console.error('Error adding note:', error);
      setError('Failed to add note. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && event.ctrlKey) {
      event.preventDefault();
      handleSubmit();
    } else if (event.key === 'Escape') {
      onClose();
    }
  };

  const handleClose = () => {
    setTitle('');
    setContent('');
    setUseVoice(false);
    setError('');
    
    // Stop recording if active
    if (isRecording) {
      toggleRecording();
    }
    
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm" 
      fullWidth
      onKeyDown={handleKeyDown}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            📝 Add Note
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={useVoice}
                onChange={handleToggleVoice}
                size="small"
              />
            }
            label="Voice"
          />
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <TextField
          autoFocus={!useVoice}
          margin="dense"
          label="Title (optional)"
          fullWidth
          variant="outlined"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          sx={{ mb: 2 }}
          placeholder="Note title"
        />
        
        {useVoice ? (
          <Box>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              mb: 2,
              p: 2,
              border: '2px dashed',
              borderColor: isRecording ? 'primary.main' : 'grey.300',
              borderRadius: 2,
              bgcolor: isRecording ? 'primary.light' : 'grey.50',
              transition: 'all 0.2s ease'
            }}>
              <IconButton
                onClick={handleToggleRecording}
                disabled={connectionStatus === 'connecting'}
                sx={{
                  bgcolor: isRecording ? 'error.main' : 'primary.main',
                  color: 'white',
                  '&:hover': {
                    bgcolor: isRecording ? 'error.dark' : 'primary.dark',
                  },
                  mr: 2
                }}
              >
                {isRecording ? <StopIcon /> : <MicIcon />}
              </IconButton>
              
              <Box>
                <Typography variant="body1" fontWeight={500}>
                  {isRecording ? 'Recording...' : 'Click to start recording'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {connectionStatus === 'connected' ? 'Connected' : 
                   connectionStatus === 'connecting' ? 'Connecting...' :
                   connectionStatus === 'reconnecting' ? 'Reconnecting...' : 'Ready'}
                </Typography>
              </Box>
            </Box>
            
            {content && (
              <TextField
                margin="dense"
                label="Transcribed Content"
                fullWidth
                multiline
                rows={4}
                variant="outlined"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                sx={{ mb: 2 }}
                helperText="You can edit the transcribed text"
              />
            )}
          </Box>
        ) : (
          <TextField
            margin="dense"
            label="Content"
            fullWidth
            multiline
            rows={6}
            variant="outlined"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Write your note here..."
          />
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={loading || (!title.trim() && !content.trim())}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Adding...' : 'Add Note'}
        </Button>
      </DialogActions>
      
      <Box sx={{ px: 2, pb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Press Ctrl+Enter to save, Esc to cancel
        </Typography>
      </Box>
    </Dialog>
  );
};

export default QuickAddNoteDialog;